import {useState} from 'react'
import {useNavigate} from 'react-router'
import {faPlus, faDownload} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import isEqual from 'lodash/isEqual'
import moment from 'moment-timezone'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {downloadFile, fancyClient} from '@freckle/educator-entities/ts/openapi-clients'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {SpinnerOverlay} from '@freckle/educator-materials/ts/components/spinner'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SchoolPicker} from '@freckle/school/ts/common/components/common/school-picker'
import ApiHelper from '@freckle/school/ts/common/helpers/api-helper'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {AddAssessmentModalContainer} from '@freckle/school/ts/pages/assessments/components/add-assessment-modal'
import AssessmentsTable from '@freckle/school/ts/pages/assessments/components/assessments-table'
import type {MathSchoolAssessmentAssignmentT} from '@freckle/school/ts/pages/assessments/models/math-school-assessment-assignment'
import {fetchMathSchoolAssessmentAssignment} from '@freckle/school/ts/pages/assessments/models/math-school-assessment-assignment'
import {YearPicker} from './year-picker'
import {iconClassName} from './assessments.module.scss'

type Props = {
  assessments: Array<MathSchoolAssessmentAssignmentT>
  teachers: Array<BaseTeacherAttrs>
  students: Array<StudentAttrs>
  mathStandardSet: RlStandardSetT
  domains: Array<RlDomainT>
  standards: Array<RlStandardWithDomainT>
  schools: Array<SchoolAttrs>
  currentSchool: SchoolAttrs
  year: number
}

export function Assessments(props: Props) {
  const {t} = useSafeTranslation()
  const {
    assessments: propsAssessments,
    currentSchool,
    students,
    teachers,
    mathStandardSet,
    domains,
    standards,
    schools,
    year
  } = props
  const navigate = useNavigate()
  const [assessments, setAssessments] =
    useState<Array<MathSchoolAssessmentAssignmentT>>(propsAssessments)
  const [showAddAssessmentModal, setShowAddAssessmentModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useSafeEffectExtraDeps<{propsAssessments: Array<MathSchoolAssessmentAssignmentT>}>(
    ({propsAssessments}) => {
      setAssessments(propsAssessments)
      setShowAddAssessmentModal(false)
      setIsLoading(false)
    },
    [],
    {
      propsAssessments: {value: propsAssessments, comparator: isEqual}
    }
  )

  function onClickAddAssessment() {
    setShowAddAssessmentModal(true)
  }

  function onHideAddAssessment() {
    setShowAddAssessmentModal(false)
  }

  function onSubmitCreateAssessment(selectedStudentIds: Array<number>) {
    const schoolId = currentSchool.id
    setShowAddAssessmentModal(false)
    setIsLoading(true)

    ApiHelper.addSchoolAssessment(schoolId, selectedStudentIds)
      .then(() => fetchMathSchoolAssessmentAssignment(schoolId, year))
      .then(
        assessments => {
          setAssessments(assessments)
          setIsLoading(false)
          Notification.displaySuccessNotification(
            t('ASSESSMENTS_PAGE_CREATE_ASSESSMENT_SUCCESS_TEXT')
          )
        },
        error => {
          setIsLoading(false)
          logErrorAsUnhandled(new HttpError(error))
          Notification.displayErrorNotification(t('ASSESSMENTS_PAGE_CREATE_ASSESSMENT_ERROR_TEXT'))
        }
      )
  }
  const dateInYear = moment({year, month: 0, day: 1})

  const downloadAssessments = () =>
    fancyClient
      .GET('/2/schools/{SchoolP-SchoolId}/assessments', {
        headers: {
          Accept: 'text/csv'
        },
        parseAs: 'blob',
        params: {
          path: {
            'SchoolP-SchoolId': currentSchool.id
          },
          query: {
            from: dateInYear.startOf('year').utc().valueOf(),
            to: dateInYear.endOf('year').utc().valueOf()
          }
        }
      })
      .then(({data, response}) => downloadFile(data, response.headers, 'assessments.csv'))

  if (isLoading) {
    return <SpinnerOverlay />
  }

  return (
    <>
      <H1>
        {t('ASSESSMENTS_PAGE_TITLE', {
          schoolName: currentSchool.name
        })}
      </H1>
      <SchoolPicker
        schools={schools}
        currentSchoolId={currentSchool.id}
        onChange={schoolId => navigate(Routes.schools.assessments(schoolId, year))}
      />
      <YearPicker
        value={year}
        onChange={year => navigate(Routes.schools.assessments(currentSchool.id, year))}
      />
      <div className="pull-right">
        <Button onClick={() => downloadAssessments()}>
          <FontAwesomeIcon className={iconClassName} icon={faDownload} />
          {t('ASSESSMENTS_PAGE_DOWNLOAD_BUTTON_TEXT')}
        </Button>{' '}
        <Button onClick={onClickAddAssessment} style="secondary" dataTest="create-assessment-btn">
          <FontAwesomeIcon icon={faPlus} /> {t('ASSESSMENTS_PAGE_CREATE_ASSESSMENT_BUTTON_TEXT')}
        </Button>
      </div>
      <AssessmentsTable
        assessments={assessments}
        schoolId={currentSchool.id}
        teachers={teachers}
        mathStandardSet={mathStandardSet}
        domains={domains}
        standards={standards}
      />
      <AddAssessmentModalContainer
        show={showAddAssessmentModal}
        onHide={onHideAddAssessment}
        students={students}
        onSubmit={selectedStudentIds => onSubmitCreateAssessment(selectedStudentIds)}
      />
    </>
  )
}
